/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled'
import { debounce } from 'lodash';

import Input from 'components/atoms/Input'
import { handleChange } from 'components/atoms/Upsert'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import * as generalHelper from 'helpers/generalHelper'
import routes from 'components/lab/constants/routes'
import Button from 'components/atoms/Button'
import DataTable from 'components/atoms/DataTable'
import { useLazyGetBillingProceduresListQuery } from 'store/services/billing-procedures';

const CellWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    white-space: nowrap;
    display: flex;
  }
`

interface SearchForm {
  name: string
  cptCode: string
}

const initialSearchVals = {
  name: '',
  cptCode: '',
}

const PAGE_SIZE = 15
const SEARCH_DELAY = 800
//
const BillingProcedures = () => {
  const navigate = useNavigate()
  const [values, setValues] = useState<SearchForm>(initialSearchVals)
  const [currentPage, setCurrentPage] = useState(0)
  const [getBillingProcedures, { currentData: billingProcedures, isFetching }] = useLazyGetBillingProceduresListQuery()

  const onSearch = () => {
    getBillingProcedures(
      {
        ...(values.name && { name: values.name }),
        ...(values.cptCode && { cptCode: values.cptCode }),
        size: PAGE_SIZE,
        page: currentPage,
      },
      false,
    )
  }

  useEffect(() => {
    onSearch();
  }, [currentPage])

  useEffect(() => {
    const searchFn = debounce(() => {
      onSearch()
    }, SEARCH_DELAY);

    searchFn();
    setCurrentPage(0);

    return () => {
      searchFn.cancel();
    };

  }, [values.name, values.cptCode])

  const headers = [
    {
      Header: 'CPT Code',
      accessor: 'cptCode',
      columnSize: 3,
      Cell: ({ row }) => (
        <CellWrapper>
          <Link to={`${routes.index}${routes.billing.root}${routes.billing.procedures}${routes.billing.edit}${row.original.id}`}>
            {row.original.cptCode}
          </Link>
        </CellWrapper>
      ),
    },
    {
      Header: 'Name',
      accessor: 'name',
      columnSize: 3,
      Cell: ({ row }) => (
        <CellWrapper>
          <div>{row.original.name}</div>
        </CellWrapper>
      ),
    },
    {
      Header: 'Short Name',
      accessor: 'shortName',
      columnSize: 3,
      Cell: ({ row }) => (
        <CellWrapper>
          <div>{row.original.shortName || '--'}</div>
        </CellWrapper>
      ),
    },
  ]

  return (
    <DashboardContent
      title="PROCEDURES"
      content={
        <div className="card card-primary">
          <div className="card-body">
            <div className="row">
              <div className="col-sm-10">
                <div className="row pl-2">
                  <div className="col-sm-2">
                    <div className="form-group">
                      <label>{generalHelper.t('Name')}</label>
                      <Input
                        value={values.name}
                        label=""
                        placeholder="Name"
                        name="name"
                        autoFocus={true}
                        handleChange={(event) => {
                          handleChange(event, values, setValues)
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            onSearch()
                          }
                        }}
                        autoComplete="one-time-code"
                      />
                    </div>
                  </div>
                  <div className="col-sm-2">
                    <div className="form-group">
                      <label htmlFor="cptCode">{generalHelper.t('CPT Code')}</label>
                      <Input
                        value={values.cptCode ?? ' '}
                        className="form-control"
                        name="cptCode"
                        placeholder="CPT Code"
                        handleChange={(event) => {
                          handleChange(event, values, setValues)
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            onSearch()
                          }
                        }}
                      />
                    </div>
                  </div>
                  </div>
                </div>
              <div className="col-sm-2">
                <Button
                  label="Add New Procedure"
                  className="btn btn-primary ml-3 position-absolute"
                  css={{ top: '16px', right: '15px' }}
                  onClick={() => navigate(`${routes.index}${routes.billing.root}${routes.billing.procedures}${routes.billing.new}`)}
                />
              </div>
              </div>
            </div>
            <div className="container-fluid">
                <DataTable
                  tableKey={`lab_billing_procedures`}
                  data={billingProcedures}
                  status={isFetching}
                  tableColumns={headers}
                  pageSize={PAGE_SIZE}
                  setCurrentPage={setCurrentPage}
                  setSortOptions={() => {}}
                  entityName="billingProcedures"
                  onRowClick={(row) =>
                    navigate(`${routes.index}${routes.billing.root}${routes.billing.procedures}${routes.billing.edit}${row.original.id}`)
                  }
                />
            </div>
          </div>
      }
      breadcrumb={[
        {
          label: 'Billing Procedures',
          to: `${routes.index}${routes.billing.root}${routes.billing.procedures}`,
        },
      ]}
    />
  )
}

export default BillingProcedures;
